<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import AvailabilityScheduler from '@/Components/Forms/AvailabilityScheduler.vue'

const props = defineProps({
  oneToOneAvailability: {
    type: Array,
    default: () => []
  },
  maxOneToOneSessions: {
    type: Number,
    default: 0
  },
  // "day-time" → the student holding it, which keeps it from being removed
  bookedOneToOneSlots: {
    type: Object,
    default: () => ({})
  }
})

// What is on screen, and what was last saved, both as "day-time" keys
const savedSlots = ref([...props.oneToOneAvailability])
const selectedOneOnOneSlots = ref([...props.oneToOneAvailability])

const oneOnOneSlotsCount = computed(() => selectedOneOnOneSlots.value.length)
const bookedCount = computed(() => Object.keys(props.bookedOneToOneSlots).length)

const isDirty = computed(() => {
  const current = new Set(selectedOneOnOneSlots.value)

  return savedSlots.value.length !== current.size || savedSlots.value.some(slot => !current.has(slot))
})
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Clear errors when slots change
watch(selectedOneOnOneSlots, () => {
  if (errors.value.oneOnOneAvailability) errors.value.oneOnOneAvailability = ''
})

const scrollToEl = (id) => {
  nextTick(() => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

const validate = () => {
  errors.value = {}

  // Check 1:1 max
  if (props.maxOneToOneSessions > 0 && selectedOneOnOneSlots.value.length > props.maxOneToOneSessions) {
    errors.value.oneOnOneAvailability = `You can select a maximum of ${props.maxOneToOneSessions} 1:1 session slot(s).`
    return false
  }

  return true
}

const saveAvailability = async () => {
  if (!validate()) return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/availability', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        one_on_one_sessions: selectedOneOnOneSlots.value
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      scrollToEl('availability-error-banner')
    } else {
      savedSlots.value = [...selectedOneOnOneSlots.value]
      successMessage.value = 'Availability updated successfully!'
      scrollToEl('availability-success-banner')
      setTimeout(() => { successMessage.value = '' }, 3000)
    }
  } catch {
    errors.value = { general: 'An error occurred. Please try again.' }
    scrollToEl('availability-error-banner')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" id="availability-success-banner" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- General Error Message -->
      <div v-if="errors.general" id="availability-error-banner" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Info Banner -->
      <div class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-blue-100">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
            <span class="material-symbols-outlined text-white text-xl">edit_calendar</span>
          </div>
          <div class="flex-1">
            <h2 class="text-xl font-bold text-gray-900 mb-2">Your Weekly Availability</h2>
            <p class="text-gray-700 leading-relaxed mb-3">
              Choose the hours you are free to teach each week, UK time, then save. New students can only book the hours you leave open.
              <template v-if="bookedCount > 0">
                A slot with a lock is held by a student, a request or a paused arrangement included, so it stays: to free it, the arrangement has to end first.
              </template>
            </p>
            <div class="flex items-center gap-2 text-sm">
              <span class="material-symbols-outlined text-blue-600">calendar_today</span>
              <span class="font-medium text-gray-900">Total Slots: <span class="text-blue-600">{{ oneOnOneSlotsCount }}</span></span>
              <span v-if="bookedCount > 0" class="text-gray-600">&middot; {{ bookedCount }} booked</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 1:1 Tutoring Sessions -->
      <div class="bg-white rounded-2xl p-6 border border-gray-200">
        <!-- 1:1 Error -->
        <div v-if="errors.oneOnOneAvailability || errors.one_on_one_sessions" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
          <div class="flex items-start gap-3">
            <span class="material-symbols-outlined shrink-0 text-[1.25rem] leading-5 text-danger">error</span>
            <p class="text-red-600 font-medium text-sm">{{ errors.oneOnOneAvailability || errors.one_on_one_sessions }}</p>
          </div>
        </div>

        <AvailabilityScheduler
          title="1:1 Tutoring Sessions"
          :description="maxOneToOneSessions > 0 ? `Select up to ${maxOneToOneSessions} weekly time slot(s) for individual tutoring` : 'Select your weekly time slots for individual tutoring'"
          icon="person"
          icon-bg-color="bg-brand-soft"
          icon-text-color="text-brand"
          :selected-slots="selectedOneOnOneSlots"
          @update:selected-slots="selectedOneOnOneSlots = $event"
          :booked-slots="bookedOneToOneSlots"
          :multi-select="true"
          selected-color="brand"
          :max-total-slots="maxOneToOneSessions"
        />
      </div>

      <!-- Save Button -->
      <div class="flex justify-end">
        <button
          @click="saveAvailability"
          :disabled="loading || !isDirty"
          class="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
          <span>{{ loading ? 'Saving...' : 'Save Availability' }}</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
